import React, { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import MultiSelect from "./MultiSelect";
import api from "../../lib/api";
import compressImage from "../../lib/compressImage";

/**
 * FieldRenderer — renders one form field based on a schema.
 * Supported types: text, email, tel, number, password, textarea, select, multi-select, image
 */
export default function FieldRenderer({ field, value, onChange, onUploadingChange }) {
  const { key, label, type = "text", placeholder, options = [], required, span = 1, accept } = field;
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const baseCls =
    "h-11 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus-visible:ring-cyan-400/60";

  let control = null;

  if (type === "textarea") {
    control = (
      <Textarea
        value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={3}
        placeholder={placeholder}
        className="bg-white/5 border-white/10 text-white placeholder:text-[#64748B] focus-visible:ring-cyan-400/60 resize-none"
        data-testid={`field-${key}`}
      />
    );
  } else if (type === "select") {
    control = (
      <Select value={value ?? ""} onValueChange={onChange} disabled={field.disabled}>
        <SelectTrigger className={baseCls} data-testid={`field-${key}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="bg-[#0B1120] border-white/10 text-white">
          {options.map((o, i) => <SelectItem key={`${o.value || "opt"}-${i}`} value={o.value} data-testid={`field-${key}-option-${o.value}`}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  } else if (type === "multi-select") {
    control = (
      <MultiSelect options={options} value={Array.isArray(value) ? value : []} onChange={onChange} placeholder={placeholder} testid={`field-${key}`} />
    );
  } else if (type === "images") {
    const images = Array.isArray(value) ? value : [];
    const onAddImage = async (e) => {
      const f = e.target.files?.[0]; if (!f) return;
      if (f.size > 2 * 1024 * 1024) { toast.error("Image too large. Max 2MB."); return; }
      setUploading(true);
      onUploadingChange?.(true);
      try {
        const compressed = await compressImage(f);
        const fd = new FormData();
        fd.append("file", compressed);
        const res = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": null },
          timeout: 120000,
        });
        onChange([...images, res.data.url]);
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || "Upload failed";
        toast.error("Image upload failed: " + msg);
      } finally {
        setUploading(false);
        onUploadingChange?.(false);
      }
    };
    const removeImage = (idx) => {
      const next = images.filter((_, i) => i !== idx);
      onChange(next);
    };
    control = (
      <div className="flex flex-wrap gap-3 items-start">
        {images.map((url, i) => (
          <div key={i} className="relative">
            <img src={url} alt="" loading="lazy" className="w-20 h-20 rounded-xl object-cover border border-white/10" />
            <button type="button" onClick={() => removeImage(i)} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#0B1120] border border-white/15 hover:bg-red-500/30 flex items-center justify-center" data-testid={`field-${key}-remove-${i}`}>
              <X size={10} />
            </button>
          </div>
        ))}
        <div className="flex flex-col items-center gap-1">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAddImage} data-testid={`field-${key}-file`} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-20 h-20 rounded-xl border border-dashed border-white/15 bg-white/5 flex items-center justify-center text-[#64748B] hover:bg-white/10 transition-colors disabled:opacity-50" data-testid={`field-${key}-button`}>
            {uploading ? <Loader2 size={20} className="animate-spin text-cyan-400" /> : <Upload size={20} />}
          </button>
          <span className="text-[10px] text-[#64748B]">{images.length} image{images.length !== 1 ? "s" : ""}</span>
        </div>
      </div>
    );
  } else if (type === "image" || type === "file") {
    const isFile = type === "file";
    const onPick = async (e) => {
      const f = e.target.files?.[0]; if (!f) return;
      const cap = isFile ? 50 * 1024 * 1024 : 2 * 1024 * 1024;
      if (f.size > cap) { toast.error(`File too large. Max ${isFile ? "50MB" : "2MB"}.`); return; }
      setUploading(true);
      onUploadingChange?.(true);
      try {
        let uploadFile = f;
        if (!isFile) {
          uploadFile = await compressImage(f);
        }
        const fd = new FormData();
        fd.append("file", uploadFile);
        const res = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": null },
          timeout: 120000,
        });
        const data = res.data;
        if (isFile) {
          onChange({ url: data.url, path: data.path, name: data.filename || f.name, type: f.type, size: f.size });
        } else {
          onChange(data.url);
        }
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || "Upload failed";
        toast.error(`${isFile ? "File" : "Image"} upload failed: ` + msg);
      } finally {
        setUploading(false);
        onUploadingChange?.(false);
      }
    };
    const v = value;
    const meta = typeof v === "object" && v && v.url ? v : (typeof v === "string" && v ? { url: v, name: "", type: "" } : null);
    const isImg = meta && (
      !isFile ||
      meta.type?.startsWith?.("image/") ||
      meta.url?.startsWith?.("data:image") || 
      meta.url?.match?.(/\.(png|jpe?g|gif|webp|svg)(\?|#|$)/i)
    );
    control = (
      <div className="flex items-center gap-3">
        {meta ? (
          isImg ? (
            <div className="relative">
              <img src={meta.url} alt="" loading="lazy" className="w-16 h-16 rounded-xl object-cover border border-white/10" />
              <button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#0B1120] border border-white/15 hover:bg-red-500/30 flex items-center justify-center" data-testid={`field-${key}-clear`}>
                <X size={10} />
              </button>
            </div>
          ) : (
            <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-[#A0ABC0] max-w-[200px] truncate" title={meta.name}>
              {meta.name || "Uploaded file"}
              <button type="button" onClick={() => onChange("")} className="ml-2 text-red-300">×</button>
            </div>
          )
        ) : (
          <div className="w-16 h-16 rounded-xl border border-dashed border-white/15 bg-white/5 flex items-center justify-center text-[#64748B]">
            {uploading ? <Loader2 size={16} className="animate-spin text-cyan-400" /> : <Upload size={16} />}
          </div>
        )}
        <input ref={fileRef} type="file" accept={accept || (isFile ? "*/*" : "image/*")} className="hidden" onChange={onPick} data-testid={`field-${key}-file`} />
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-white/5 border-white/10 text-white hover:bg-white/10 disabled:opacity-50" data-testid={`field-${key}-button`}>
          {uploading ? <Loader2 size={12} className="animate-spin mr-2" /> : <Upload size={12} className="mr-2" />}
          {uploading ? "Uploading…" : "Upload"}
        </Button>
      </div>
    );
  } else {
    control = (
      <Input
        type={type}
        value={value ?? ""} onChange={(e) => onChange(type === "number" ? e.target.value : e.target.value)}
        placeholder={placeholder} className={baseCls}
        minLength={field.minLength}
        data-testid={`field-${key}`}
      />
    );
  }

  return (
    <div className={span === 2 ? "md:col-span-2 space-y-2" : "space-y-2"}>
      <Label className="text-sm font-medium text-[#A0ABC0]">
        {label} {required && <span className="text-cyan-300">*</span>}
      </Label>
      {control}
    </div>
  );
}
